export type WelcomeRecoveryStatus =
  | 'current_device_ready'
  | 'proof_pending'
  | 'provider_proved'
  | 'unavailable';

export interface WelcomeRecoveryStatusView {
  status: WelcomeRecoveryStatus;
  title: string;
  description: string;
}

export interface WelcomeRecoveryEvidence {
  hasCurrentDeviceIdentity?: boolean;
  providerProofAvailable?: boolean;
  evidenceReadable?: boolean;
}

export interface WelcomeRecoveryStatusAdapter {
  read(evidence?: WelcomeRecoveryEvidence): WelcomeRecoveryStatusView;
}

const RECOVERY_STATUS_COPY: Record<WelcomeRecoveryStatus, WelcomeRecoveryStatusView> = {
  current_device_ready: {
    status: 'current_device_ready',
    title: 'Current device ready',
    description: 'Your current session can use local identity keys. Fresh-device recovery is not claimed here.',
  },
  proof_pending: {
    status: 'proof_pending',
    title: 'Recovery proof pending',
    description: 'Fresh-device recovery is still being proved, so Welcome will not claim it is ready.',
  },
  provider_proved: {
    status: 'provider_proved',
    title: 'Recovery provider proved',
    description: 'A real provider-backed recovery proof is available for this identity.',
  },
  unavailable: {
    status: 'unavailable',
    title: 'Recovery status unavailable',
    description: 'Recovery evidence is unavailable, so OwnYou will not claim fresh-device recovery.',
  },
};

export function getConservativeRecoveryStatus(hasIdentity: boolean): WelcomeRecoveryStatusView {
  return hasIdentity ? RECOVERY_STATUS_COPY.current_device_ready : RECOVERY_STATUS_COPY.unavailable;
}

export function resolveWelcomeRecoveryStatus(evidence?: WelcomeRecoveryEvidence): WelcomeRecoveryStatusView {
  if (!evidence || evidence.evidenceReadable === false) {
    return RECOVERY_STATUS_COPY.unavailable;
  }

  if (evidence.providerProofAvailable === true) {
    return RECOVERY_STATUS_COPY.provider_proved;
  }

  if (evidence.hasCurrentDeviceIdentity === true) {
    return RECOVERY_STATUS_COPY.current_device_ready;
  }

  return RECOVERY_STATUS_COPY.proof_pending;
}

export function getRecoveryStatusView(status: WelcomeRecoveryStatus): WelcomeRecoveryStatusView {
  return RECOVERY_STATUS_COPY[status];
}

export function createConservativeWelcomeRecoveryStatusAdapter(): WelcomeRecoveryStatusAdapter {
  return {
    read: (evidence) => resolveWelcomeRecoveryStatus(evidence),
  };
}
